import { apiClient } from './client';

export const getRelayStatus = () => apiClient.get('/relay/status');
export const listRooms = () => apiClient.get('/relay/rooms');
export const getRoom = (id) => apiClient.get(`/relay/rooms/${id}`);
export const getJobRoom = (jobId) => apiClient.get(`/relay/rooms/job/${jobId}`);
export const createRoom = (payload) => apiClient.post('/relay/rooms', payload);
export const listPresetChannels = () => apiClient.get('/relay/preset-channels');
export const addPresetChannel = (key, memberIds) =>
  apiClient.post(`/relay/preset-channels/${key}`, { member_ids: memberIds || [] });
export const addRoomMembers = (id, userIds) =>
  apiClient.post(`/relay/rooms/${id}/members`, { user_ids: userIds });
export const removeRoomMember = (id, memberId) =>
  apiClient.delete(`/relay/rooms/${id}/members/${memberId}`);
export const archiveRoom = (id) => apiClient.post(`/relay/rooms/${id}/archive`);
// LOOP W7 — rename a channel and/or set its topic (creator-or-manager).
export const updateRoom = (id, patch) => apiClient.patch(`/relay/rooms/${id}`, patch);
// LOOP W7 — restore an archived channel + read the archived list.
export const unarchiveRoom = (id) => apiClient.post(`/relay/rooms/${id}/unarchive`);
export const listArchivedRooms = () => apiClient.get('/relay/rooms/archived');
// LOOP W7 — start a DM. A single id → 1:1 (`{user_id}`, unchanged); an array of
// 2..7 others → a group DM (`{user_ids}` at POST /relay/dms).
export const startDm = (userIds) => {
  const arr = Array.isArray(userIds) ? userIds.filter(Boolean) : [userIds].filter(Boolean);
  return arr.length > 1
    ? apiClient.post('/relay/dms', { user_ids: arr })
    : apiClient.post('/relay/dm', { user_id: arr[0] });
};
export const listRelayMembers = () => apiClient.get('/relay/members');

// Phase 3 — messages
export const listRoomMessages = (roomId, params, { pollTag } = {}) =>
  apiClient.get(`/relay/rooms/${roomId}/messages`, {
    params,
    ...(pollTag ? { headers: { 'X-Trigger': `poll:${pollTag}` } } : {}),
  });
export const sendMessage = (roomId, payload) =>
  apiClient.post(`/relay/rooms/${roomId}/messages`, payload);
// LOOP W6 — full-text message search (company-scoped, visibility-gated).
export const searchMessages = (params) =>
  apiClient.get('/relay/search', { params });
export const editMessage = (messageId, body) =>
  apiClient.patch(`/relay/messages/${messageId}`, { body });
export const deleteMessage = (messageId) =>
  apiClient.delete(`/relay/messages/${messageId}`);
export const restoreMessage = (messageId) =>
  apiClient.post(`/relay/messages/${messageId}/restore`);
export const sendTyping = (roomId) => apiClient.post(`/relay/rooms/${roomId}/typing`);

// LOOP W5 — emoji reactions
export const addReaction = (messageId, emoji) =>
  apiClient.post(`/relay/messages/${messageId}/reactions`, { emoji });
export const removeReaction = (messageId, emoji) =>
  apiClient.delete(`/relay/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`);

// Phase 7 — slash command actions
export const runRelayNote = (roomId, payload) =>
  apiClient.post(`/relay/rooms/${roomId}/commands/note`, payload);

// Per-room mute + company settings
export const runRelayStatus = (roomId, payload) =>
  apiClient.post(`/relay/rooms/${roomId}/commands/status`, payload);
export const setRoomSystemMute = (roomId, muted) =>
  apiClient.post(`/relay/rooms/${roomId}/mute`, { muted });
export const setRoomNotifyLevel = (roomId, level) =>
  apiClient.post(`/relay/rooms/${roomId}/notify-level`, { level });
export const getRelaySettings = () => apiClient.get('/relay/settings');
export const updateRelaySettings = (patch) => apiClient.put('/relay/settings', patch);

// Auto-Channels (room templates)
export const listRelayTemplates = () => apiClient.get('/relay/templates');
export const createRelayTemplate = (payload) => apiClient.post('/relay/templates', payload);
export const updateRelayTemplate = (key, patch) => apiClient.put(`/relay/templates/${key}`, patch);
export const deleteRelayTemplate = (id) => apiClient.delete(`/relay/templates/${id}`);

// Phase 6 — attachments
export const uploadRelayFile = (roomId, file, onUploadProgress) => {
  const form = new FormData();
  form.append('file', file);
  return apiClient.post(`/relay/rooms/${roomId}/uploads`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
  });
};

export const relayFileUrl = (fileId, opts = {}) => {
  const base = process.env.REACT_APP_BACKEND_URL;
  const params = new URLSearchParams();
  if (opts.size) params.set('size', opts.size);
  if (opts.disposition) params.set('disposition', opts.disposition);
  const qs = params.toString();
  return `${base}/api/relay/files/${fileId}/content${qs ? `?${qs}` : ''}`;
};

// LOOP W9-Lite — per-room Files tab: paginated attachment index (keyset by
// created_at). Pass `before` (an ISO created_at cursor) to page older.
export const getRoomFiles = (roomId, { limit, before } = {}) => {
  const params = {};
  if (limit) params.limit = limit;
  if (before) params.before = before;
  return apiClient.get(`/relay/rooms/${roomId}/files`, { params });
};

// Phase 5 — unread + threads
export const getUnread = () => apiClient.get('/relay/unread');
// Mark a room read. Bare call = read up to now. Optional `at` (ISO) moves the
// watermark to that time — including BACKWARD, for "mark unread from here".
export const markRoomRead = (roomId, at) =>
  apiClient.post(`/relay/rooms/${roomId}/read`, at ? { at } : undefined);
// Read receipts — {user_id: last_read_at}. A 404 means Relay/receipts aren't
// available; callers swallow it and hide the "Seen" UI.
export const getRoomReceipts = (roomId, { pollTag } = {}) =>
  apiClient.get(`/relay/rooms/${roomId}/receipts`, {
    ...(pollTag ? { headers: { 'X-Trigger': `poll:${pollTag}` } } : {}),
  });
export const listThread = (roomId, messageId) =>
  apiClient.get(`/relay/rooms/${roomId}/messages/${messageId}/thread`);
